import { Button, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { getLocale } from "../../../i18n";
import { useTranslation } from "../../../i18n/useTranslation";
import { AuthService } from "../../../services";
import useNoti from "../../../hooks/useNoti";
import { AppNotifications } from "../../../models";
import Loading from "../../components/Loading";
import "./index.scss";

type ProfileData = {
  id?: string;
  code?: string;
  name?: string;
  email?: string | null;
  avatar?: string | null;
  customerID?: string;
};

type SessionItem = {
  id: string;
  username: string;
  ip: string | null;
  userAgent: string | null;
  isActive: boolean;
  isCurrent: boolean;
  createdAt: string;
  lastSeenAt: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const [pushNoti] = useNoti();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      const res = await AuthService.authProfile();
      setProfile(res.data?.data || null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const res = await AuthService.listSessions();
      setSessions(res.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const changePassword = async () => {
    if (newPassword.length < 8) {
      pushNoti(AppNotifications.ERROR, t("profile.passwordMin"));
      return;
    }
    if (newPassword !== confirmPassword) {
      pushNoti(AppNotifications.ERROR, t("profile.passwordMismatch"));
      return;
    }
    setSaving(true);
    try {
      await AuthService.changePassword({
        currentPassword,
        newPassword,
      });
      pushNoti(AppNotifications.SUCCESS, t("profile.passwordChanged"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ERROR";
      pushNoti(AppNotifications.ERROR, String(message));
    } finally {
      setSaving(false);
    }
  };

  const revokeSession = async (sessionId: string) => {
    setRevokingId(sessionId);
    try {
      await AuthService.revokeSession(sessionId);
      pushNoti(AppNotifications.SUCCESS, t("profile.sessionRevoked"));
      fetchSessions();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ERROR";
      pushNoti(AppNotifications.ERROR, String(message));
    } finally {
      setRevokingId(null);
    }
  };

  const revokeOthers = async () => {
    setRevokingId("others");
    try {
      const res = await AuthService.revokeOtherSessions();
      const count = res.data?.data?.revokedCount || 0;
      pushNoti(AppNotifications.SUCCESS, t("profile.sessionsRevokedOthers", { count }));
      fetchSessions();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ERROR";
      pushNoti(AppNotifications.ERROR, String(message));
    } finally {
      setRevokingId(null);
    }
  };

  if (loadingProfile || !profile) {
    return <Loading />;
  }

  return (
    <div className="profile-page">
      <h2>{t("profile.title")}</h2>

      <div className="profile-page__grid">
        <section className="profile-page__card">
          <h3>{t("profile.accountInfo")}</h3>
          <div className="profile-page__field">
            <span>{t("profile.company")}</span>
            <strong>{profile.name || "-"}</strong>
          </div>
          <div className="profile-page__field">
            <span>{t("profile.username")}</span>
            <strong>{profile.code || "-"}</strong>
          </div>
          <div className="profile-page__field">
            <span>Email</span>
            <strong>{profile.email || "-"}</strong>
          </div>
          <div className="profile-page__field">
            <span>{t("profile.customerId")}</span>
            <strong>{profile.customerID || "-"}</strong>
          </div>
        </section>

        <section className="profile-page__card">
          <h3>{t("profile.changePassword")}</h3>
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label={t("profile.currentPassword")}
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label={t("profile.newPassword")}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label={t("profile.confirmPassword")}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
          <Button variant="contained" disabled={saving} onClick={changePassword}>
            {t("profile.savePassword")}
          </Button>
        </section>
      </div>

      <section className="profile-page__card profile-page__sessions">
        <div className="profile-page__section-header">
          <h3>{t("profile.sessionsTitle")}</h3>
          <Button disabled={revokingId === "others"} onClick={revokeOthers}>
            {t("profile.revokeOthers")}
          </Button>
        </div>
        {loadingSessions ? (
          <Loading />
        ) : (
          <div className="profile-page__table">
            <div className="profile-page__row profile-page__row--head">
              <div>{t("profile.sessionUser")}</div>
              <div>IP</div>
              <div>{t("profile.sessionCreated")}</div>
              <div>{t("common.status")}</div>
              <div>{t("common.actions")}</div>
            </div>
            {sessions.map((session) => (
              <div className="profile-page__row" key={session.id}>
                <div>
                  <strong>{session.username || "-"}</strong>
                  <div className="profile-page__muted">{session.userAgent || "-"}</div>
                </div>
                <div>{session.ip || "-"}</div>
                <div>{formatDateTime(session.createdAt)}</div>
                <div>
                  {session.isCurrent
                    ? t("profile.sessionCurrent")
                    : session.isActive
                    ? t("profile.sessionActive")
                    : t("profile.sessionRevokedLabel")}
                </div>
                <div>
                  {session.isActive && !session.isCurrent ? (
                    <Button
                      size="small"
                      disabled={revokingId === session.id}
                      onClick={() => revokeSession(session.id)}
                    >
                      {t("profile.revokeSession")}
                    </Button>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
